import React, { useMemo, useState } from 'react';
import { HeagleRenderer } from '../types';

interface RendererFilterProps {
  renderers: HeagleRenderer[];
  selectedRendererIds: number[];
  selectedRendererGroups: string[];
  onRendererIdsChange: (ids: number[]) => void;
  onRendererGroupsChange: (groups: string[]) => void;
}

const RendererFilter: React.FC<RendererFilterProps> = ({
  renderers,
  selectedRendererIds,
  selectedRendererGroups,
  onRendererIdsChange,
  onRendererGroupsChange,
}) => {
  const [idInput, setIdInput] = useState('');
  const [groupInput, setGroupInput] = useState('');
  // 고유한 renderer_id 목록
  const uniqueRendererIds = useMemo(() => {
    return Array.from(new Set(renderers.map(r => r.renderer_id))).sort((a, b) => a - b);
  }, [renderers]);

  // 고유한 renderer_group 목록 (null 제외)
  const uniqueRendererGroups = useMemo(() => {
    const groups = renderers
      .map(r => r.renderer_group)
      .filter((group): group is string => group !== null && group !== '');
    return Array.from(new Set(groups)).sort();
  }, [renderers]);

  const handleRendererIdToggle = (id: number) => {
    if (selectedRendererIds.includes(id)) {
      onRendererIdsChange(selectedRendererIds.filter(selectedId => selectedId !== id));
    } else {
      onRendererIdsChange([...selectedRendererIds, id]);
    }
  };

  const handleSelectAllIds = () => {
    if (selectedRendererIds.length === uniqueRendererIds.length) {
      onRendererIdsChange([]);
    } else {
      onRendererIdsChange([...uniqueRendererIds]);
    }
  };

  const handleClearFilters = () => {
    onRendererIdsChange([]);
    onRendererGroupsChange([]);
    setIdInput('');
    setGroupInput('');
  };

  const handleIdInputChange = (value: string) => {
    setIdInput(value);
  };

  const handleAddIdsFromInput = (inputValue?: string) => {
    const valueToProcess = inputValue !== undefined ? inputValue : idInput;
    if (!valueToProcess.trim()) return;

    // 쉼표, 공백, 줄바꿈으로 구분된 ID 파싱
    const inputIds = valueToProcess
      .split(/[,\s\n]+/)
      .map(id => id.trim())
      .filter(id => id !== '')
      .map(id => parseInt(id, 10))
      .filter(id => !isNaN(id) && id > 0);

    if (inputIds.length > 0) {
      // 기존 선택된 ID와 합치고 중복 제거
      const newIds = Array.from(new Set([...selectedRendererIds, ...inputIds]));
      onRendererIdsChange(newIds);
      setIdInput('');
    }
  };

  const handleIdInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === ' ') {
      // 스페이스바를 누르면 현재 입력된 값을 추가
      e.preventDefault();
      const currentValue = idInput.trim();
      if (currentValue) {
        handleAddIdsFromInput(currentValue);
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      handleAddIdsFromInput();
    } else if (e.key === 'Backspace' && idInput === '' && selectedRendererIds.length > 0) {
      // 입력 필드가 비어있고 Backspace를 누르면 마지막 태그 삭제
      e.preventDefault();
      onRendererIdsChange(selectedRendererIds.slice(0, -1));
    }
  };

  const handleRemoveTag = (idToRemove: number) => {
    onRendererIdsChange(selectedRendererIds.filter(id => id !== idToRemove));
  };

  const handleGroupInputChange = (value: string) => {
    setGroupInput(value);
  };

  const handleAddGroupsFromInput = (inputValue?: string) => {
    const valueToProcess = inputValue !== undefined ? inputValue : groupInput;
    if (!valueToProcess.trim()) return;

    // 쉼표, 공백, 줄바꿈으로 구분된 Group 파싱
    const inputGroups = valueToProcess
      .split(/[,\s\n]+/)
      .map(group => group.trim())
      .filter(group => group !== '');

    if (inputGroups.length > 0) {
      // 기존 선택된 Group과 합치고 중복 제거
      const newGroups = Array.from(new Set([...selectedRendererGroups, ...inputGroups]));
      onRendererGroupsChange(newGroups);
      setGroupInput('');
    }
  };

  const handleGroupInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === ' ') {
      // 스페이스바를 누르면 현재 입력된 값을 추가
      e.preventDefault();
      const currentValue = groupInput.trim();
      if (currentValue) {
        handleAddGroupsFromInput(currentValue);
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      handleAddGroupsFromInput();
    } else if (e.key === 'Backspace' && groupInput === '' && selectedRendererGroups.length > 0) {
      // 입력 필드가 비어있고 Backspace를 누르면 마지막 태그 삭제
      e.preventDefault();
      onRendererGroupsChange(selectedRendererGroups.slice(0, -1));
    }
  };

  const handleRemoveGroupTag = (groupToRemove: string) => {
    onRendererGroupsChange(selectedRendererGroups.filter(group => group !== groupToRemove));
  };

  const handleRendererGroupToggle = (group: string) => {
    if (selectedRendererGroups.includes(group)) {
      onRendererGroupsChange(selectedRendererGroups.filter(selectedGroup => selectedGroup !== group));
    } else {
      onRendererGroupsChange([...selectedRendererGroups, group]);
    }
  };

  const handleSelectAllGroups = () => {
    if (selectedRendererGroups.length === uniqueRendererGroups.length) {
      onRendererGroupsChange([]);
    } else {
      onRendererGroupsChange([...uniqueRendererGroups]);
    }
  };

  return (
    <div className="renderer-filter">
      <div className="filter-header">
        <h3>필터</h3>
        <button 
          className="clear-filter-button"
          onClick={handleClearFilters}
        >
          필터 초기화
        </button>
      </div>
      
      <div className="filter-controls">
        <div className="filter-group">
          <label className="filter-label">Renderer ID (복수 선택 가능)</label>
          <div className="filter-actions">
            <button 
              className="select-all-button"
              onClick={handleSelectAllIds}
            >
              {selectedRendererIds.length === uniqueRendererIds.length ? '전체 해제' : '전체 선택'}
            </button>
            <span className="filter-count">
              선택됨: {selectedRendererIds.length} / {uniqueRendererIds.length}
            </span>
          </div>
          <div className="id-input-group">
            <div className="tag-input-container">
              {selectedRendererIds.map(id => (
                <span key={id} className="id-tag" onClick={() => handleRemoveTag(id)}>
                  {id}
                  <span className="tag-remove">×</span>
                </span>
              ))}
              <input
                type="text"
                className="id-input"
                placeholder={selectedRendererIds.length === 0 ? "ID 입력 후 스페이스바 또는 Enter로 추가" : ""}
                value={idInput}
                onChange={(e) => handleIdInputChange(e.target.value)}
                onKeyDown={handleIdInputKeyDown}
              />
            </div>
            <button
              className="add-id-button"
              onClick={() => handleAddIdsFromInput()}
            >
              추가
            </button>
          </div>
          <div className="checkbox-group">
            {uniqueRendererIds.map(id => (
              <label key={id} className="checkbox-label">
                <input
                  type="checkbox"
                  checked={selectedRendererIds.includes(id)}
                  onChange={() => handleRendererIdToggle(id)}
                />
                <span>{id}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="filter-group">
          <label className="filter-label">Renderer Group (복수 선택 가능)</label>
          <div className="filter-actions">
            <button 
              className="select-all-button"
              onClick={handleSelectAllGroups}
            >
              {selectedRendererGroups.length === uniqueRendererGroups.length ? '전체 해제' : '전체 선택'}
            </button>
            <span className="filter-count">
              선택됨: {selectedRendererGroups.length} / {uniqueRendererGroups.length}
            </span>
          </div>
          <div className="id-input-group">
            <div className="tag-input-container">
              {selectedRendererGroups.map(group => (
                <span key={group} className="id-tag" onClick={() => handleRemoveGroupTag(group)}>
                  {group}
                  <span className="tag-remove">×</span>
                </span>
              ))}
              <input
                type="text"
                className="id-input"
                placeholder={selectedRendererGroups.length === 0 ? "Group 입력 후 스페이스바 또는 Enter로 추가" : ""}
                value={groupInput}
                onChange={(e) => handleGroupInputChange(e.target.value)}
                onKeyDown={handleGroupInputKeyDown}
              />
            </div>
            <button
              className="add-id-button"
              onClick={() => handleAddGroupsFromInput()}
            >
              추가
            </button>
          </div>
          <div className="checkbox-group">
            {uniqueRendererGroups.map(group => (
              <label key={group} className="checkbox-label">
                <input
                  type="checkbox"
                  checked={selectedRendererGroups.includes(group)}
                  onChange={() => handleRendererGroupToggle(group)}
                />
                <span>{group}</span>
              </label>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RendererFilter;

